import React, { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { Download, X, Smartphone, Apple } from "lucide-react";

/**
 * PWA install prompt — desktop (beforeinstallprompt) + iOS Safari helper.
 *
 * Persistent re-targeting (P1 — 2026-02-15):
 *  - Once dismissed, the prompt re-appears after a back-off window:
 *      1st dismiss → 3 days
 *      2nd dismiss → 7 days
 *      3rd dismiss → 30 days
 *      4th+ dismiss → "never" (we respect the user's stance after 3 retries)
 *  - "appinstalled" → permanent (never show again).
 *
 * Storage shape (localStorage key `geovinum_pwa_install_v3`):
 *   { status: "dismissed"|"installed", count: int, nextShowAt: ISO }
 */
const STORAGE_KEY = "geovinum_pwa_install_v3";
const LEGACY_KEY = "geovinum_pwa_install_v2"; // older single-string key

const BACKOFF_DAYS = [3, 7, 30];

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIosSafari() {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function readState() {
  // Migrate legacy "dismissed" entry to v3 with one back-off pass
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy && !localStorage.getItem(STORAGE_KEY)) {
      const isInstalled = legacy === "installed";
      const initial = isInstalled
        ? { status: "installed", count: 0, nextShowAt: null }
        : { status: "dismissed", count: 1, nextShowAt: new Date(Date.now() + BACKOFF_DAYS[0] * 86400000).toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
      localStorage.removeItem(LEGACY_KEY);
    }
  } catch {
    /* ignore */
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

function shouldShow(state) {
  if (!state) return true;
  if (state.status === "installed") return false;
  if (!state.nextShowAt) return true;
  return new Date(state.nextShowAt).getTime() <= Date.now();
}

export default function PwaInstallPrompt() {
  const { lang } = useT();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const state = readState();
    if (!shouldShow(state)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setShow(true), 3500);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    let iosTimer = null;
    if (isIosSafari()) {
      iosTimer = setTimeout(() => { setIosHelp(true); setShow(true); }, 7000);
    }

    const onInstalled = () => {
      writeState({ status: "installed", count: 0, nextShowAt: null });
      setShow(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    const prev = readState() || { count: 0 };
    const newCount = (prev.count || 0) + 1;
    const days = BACKOFF_DAYS[Math.min(newCount - 1, BACKOFF_DAYS.length - 1)];
    const nextShowAt = newCount > BACKOFF_DAYS.length
      ? null  // never show again after exhausting all back-offs
      : new Date(Date.now() + days * 86400000).toISOString();
    const status = newCount > BACKOFF_DAYS.length ? "installed" /* sentinel */ : "dismissed";
    writeState({ status, count: newCount, nextShowAt });
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      writeState({ status: "installed", count: 0, nextShowAt: null });
    } else {
      dismiss(); // count as a soft dismiss
    }
    setDeferred(null);
    setShow(false);
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-[calc(var(--m-tabs-h)_+_0.75rem)] left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 rounded-2xl bg-white border border-[#E5276A]/20 shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-[#FCE4ED] flex items-center justify-center shrink-0">
        {iosHelp ? <Apple className="w-5 h-5 text-[#E5276A]" /> : <Smartphone className="w-5 h-5 text-[#E5276A]" />}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-[#1F3A5F] text-sm">
          {lang === "fr" ? "Installer geoVINUM" : "Install geoVINUM"}
        </h3>
        {iosHelp ? (
          <p className="text-xs text-[#4A5568] mt-1">
            {lang === "fr"
              ? <>Appuyez sur <b>Partager</b> dans Safari puis <b>« Sur l&apos;écran d&apos;accueil »</b>.</>
              : <>Tap <b>Share</b> in Safari then <b>&ldquo;Add to Home Screen&rdquo;</b>.</>}
          </p>
        ) : (
          <p className="text-xs text-[#4A5568] mt-1">
            {lang === "fr"
              ? "Accès rapide depuis votre écran d'accueil, hors-ligne et notifications."
              : "Quick access from your home screen, offline & notifications."}
          </p>
        )}
        <div className="mt-3 flex gap-2">
          {!iosHelp && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]"
            >
              <Download className="w-3.5 h-3.5" /> {lang === "fr" ? "Installer" : "Install"}
            </button>
          )}
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismiss}
            className="text-xs text-[#8B95A1] hover:text-[#1F3A5F] underline"
          >
            {lang === "fr" ? "Plus tard" : "Later"}
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-[#8B95A1] hover:text-[#1F3A5F] shrink-0"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
